/* ClientExperiencePage.jsx — The Client Confidence + Reassurance Layer.
 *
 * Premium, calm, emotionally intelligent transaction experience for
 * buyers and sellers. Hides every operational complexity (risk scoring,
 * chain fragility, escalation language) behind a single composed payload
 * from the backend `client_experience_engine`.
 *
 * Routes
 *   /client-view                      — list of the user's deals
 *   /client-view/:dealId              — authenticated client experience
 *   /client-view/by-token/:token      — unauthenticated magic-link view
 *
 * Aesthetic
 *   • Cinematic gradient hero · soft glass cards · calm spacing
 *   • No CRM chrome · no operational vocabulary · luxury concierge tone
 *   • Mobile-first stacking · thumb-friendly touch targets · large type
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Handshake, FileCheck, Scale, Search, ShieldCheck, Calendar, Sparkles,
  Home, Circle, Wallet, FileText, PenLine, Briefcase, User,
  Mail, Phone, Upload, Camera, ChevronRight, CheckCircle2, Clock,
  ArrowLeft, Heart, MessageCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { clientPreflight, uploadErrorPresentation } from "../lib/uploadErrors";

const ICON_MAP = {
  Handshake, FileCheck, Scale, Search, ShieldCheck, Calendar, Sparkles,
  Home, Circle, Wallet, FileText, PenLine, Briefcase, User,
};

const TONE = {
  positive: { fg: "#047857", bg: "rgba(16,185,129,0.14)", dot: "#10B981" },
  neutral:  { fg: "#4338CA", bg: "rgba(99,102,241,0.10)", dot: "#818CF8" },
  calm:     { fg: "#047857", bg: "rgba(16,185,129,0.10)", dot: "#10B981" },
  important:{ fg: "#92400E", bg: "rgba(245,158,11,0.12)", dot: "#F59E0B" },
};

const Icon = ({ name, size = 14, ...rest }) => {
  const Cmp = ICON_MAP[name] || Circle;
  return <Cmp size={size} strokeWidth={2.2} {...rest} />;
};

/* ─── Hero ─────────────────────────────────────────────────────── */
const ClientHero = ({ deal, stageView, reassurance }) => {
  const tone = TONE[stageView?.confidence_tone] || TONE.neutral;
  return (
    <header
      className="relative overflow-hidden rounded-3xl px-6 sm:px-9 py-8 sm:py-10 mb-6"
      data-testid="client-hero"
      style={{
        background: "linear-gradient(135deg, #0F172A 0%, #2A2566 55%, #5B4FCF 100%)",
        boxShadow: "0 30px 80px -36px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
      }}
    >
      <span aria-hidden className="absolute -top-16 -left-10 w-[320px] h-[320px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(167,139,250,0.45), transparent 65%)" }} />
      <span aria-hidden className="absolute -bottom-20 right-8 w-[380px] h-[380px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(56,189,248,0.22), transparent 70%)" }} />
      <div className="relative">
        <div className="inline-flex items-center gap-2 mb-3">
          <Heart size={12} className="text-pink-300" strokeWidth={2.3} />
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-pink-200/90">
            Your Transaction
          </p>
        </div>
        <h1
          className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-[1.1] mb-2"
          data-testid="client-hero-deal-name"
        >
          {deal?.name || "Your transaction"}
        </h1>

        <div className="inline-flex items-center gap-2 mt-3 mb-4 px-3 py-1.5 rounded-full"
          style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }}>
          <Icon name={stageView?.icon} size={13} className="text-white" />
          <p className="text-[12px] font-semibold text-white">
            {stageView?.current_label}
          </p>
          <span aria-hidden className="w-1 h-1 rounded-full bg-white/40" />
          <p className="text-[11.5px] text-white/70 font-medium">
            Next: {stageView?.next_milestone}
          </p>
        </div>

        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ background: `${tone.dot}22`, border: `1px solid ${tone.dot}44` }}
          data-testid="client-confidence-pill"
        >
          <span aria-hidden className="w-2 h-2 rounded-full" style={{ background: tone.dot }} />
          <p className="text-[11.5px] font-semibold" style={{ color: tone.dot }}>
            {stageView?.confidence_label}
          </p>
        </div>

        {reassurance ? (
          <p className="text-[13.5px] sm:text-[14px] text-white/80 leading-relaxed max-w-2xl mt-5"
             data-testid="client-reassurance">
            {reassurance}
          </p>
        ) : null}
      </div>
    </header>
  );
};

/* ─── Simplified Timeline ──────────────────────────────────────── */
const SimplifiedTimeline = ({ items }) => {
  if (!items?.length) return null;
  return (
    <section className="mb-6" data-testid="client-timeline">
      <SectionHeader title="Your transaction journey" subtitle="Simple, clear progression." />
      <div className="gp-card p-5">
        <div className="flex md:hidden flex-col space-y-3">
          {items.map((n) => (
            <TimelineRow key={n.key} node={n} />
          ))}
        </div>
        <div className="hidden md:flex items-stretch gap-1 overflow-x-auto pb-1">
          {items.map((n, i) => (
            <TimelineNode key={n.key} node={n} isLast={i === items.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

const TimelineRow = ({ node }) => {
  const isDone = node.state === "completed";
  const isCurrent = node.state === "current";
  const bg = isDone ? "#10B981" : isCurrent ? "#818CF8" : "rgba(148,163,184,0.4)";
  const fg = isDone ? "#FFFFFF" : isCurrent ? "#FFFFFF" : "#94A3B8";
  return (
    <div className="flex items-center gap-3" data-testid={`client-timeline-row-${node.key}`}>
      <div className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0"
        style={{ background: bg, color: fg }}>
        {isDone ? <CheckCircle2 size={14} strokeWidth={2.5} /> : <Icon name={node.icon} size={13} />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold text-slate-900 truncate">{node.label}</p>
        {node.date_hint ? (
          <p className="text-[11px] text-slate-500">{node.date_hint}</p>
        ) : isCurrent ? (
          <p className="text-[11px] text-violet-600 font-medium">In progress</p>
        ) : null}
      </div>
    </div>
  );
};

const TimelineNode = ({ node, isLast }) => {
  const isDone = node.state === "completed";
  const isCurrent = node.state === "current";
  const bg = isDone ? "#10B981" : isCurrent ? "#818CF8" : "rgba(148,163,184,0.4)";
  return (
    <div className="flex-1 min-w-[100px] flex flex-col items-center text-center"
         data-testid={`client-timeline-node-${node.key}`}>
      <div className="flex items-center w-full mb-2">
        <span className="flex-1 h-0.5" style={{ background: isDone ? "#10B981" : "rgba(148,163,184,0.18)" }} />
        <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0"
          style={{
            background: bg, color: "#FFFFFF",
            boxShadow: isCurrent ? "0 0 0 4px rgba(129,140,248,0.18)" : "none",
          }}>
          {isDone ? <CheckCircle2 size={14} strokeWidth={2.5} /> : <Icon name={node.icon} size={13} />}
        </div>
        <span className="flex-1 h-0.5"
          style={{ background: isLast ? "transparent" : (isDone ? "#10B981" : "rgba(148,163,184,0.18)") }} />
      </div>
      <p className="text-[10.5px] font-semibold text-slate-800 leading-tight">{node.label}</p>
      {node.date_hint ? (
        <p className="text-[10px] text-slate-500 mt-0.5">{node.date_hint}</p>
      ) : isCurrent ? (
        <p className="text-[10px] text-violet-600 font-medium mt-0.5">Now</p>
      ) : null}
    </div>
  );
};

/* ─── Action Centre ────────────────────────────────────────────── */
const ActionCentre = ({ actions, onUploadClick }) => {
  if (!actions?.length) return null;
  return (
    <section className="mb-6" data-testid="client-action-centre">
      <SectionHeader title="Your action centre" subtitle="Quick things you can do to keep things moving." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {actions.map((a) => {
          const tone = TONE[a.tone] || TONE.neutral;
          return (
            <button
              type="button"
              key={a.key}
              onClick={() => a.doc_type ? onUploadClick?.(a) : null}
              className="gp-card p-4 text-left transition-all hover:shadow-md"
              style={{ borderLeft: `3px solid ${tone.dot}` }}
              data-testid={`client-action-${a.key}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0"
                  style={{ background: tone.bg, color: tone.fg }}>
                  <Icon name={a.icon} size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-slate-900 leading-snug mb-1">{a.label}</p>
                  {a.why ? (
                    <p className="text-[11.5px] text-slate-600 leading-relaxed">{a.why}</p>
                  ) : null}
                  <div className="flex items-center gap-2 mt-2">
                    {a.est_minutes ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                        <Clock size={9} strokeWidth={2.4} />
                        {a.est_minutes} min
                      </span>
                    ) : null}
                    {a.impact ? (
                      <span className="text-[10px] text-slate-500 truncate">· {a.impact}</span>
                    ) : null}
                  </div>
                </div>
                {a.tone !== "calm" ? (
                  <ChevronRight size={13} className="text-slate-300 shrink-0" />
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

/* ─── Key Contacts ─────────────────────────────────────────────── */
const KeyContacts = ({ contacts }) => {
  if (!contacts?.length) return null;
  return (
    <section className="mb-6" data-testid="client-contacts">
      <SectionHeader title="Your key contacts" subtitle="Reach the right person, quickly." />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {contacts.map((c) => (
          <div
            key={`${c.role_key}-${c.name}`}
            className="gp-card p-4"
            data-testid={`client-contact-${c.role_key}`}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-full inline-flex items-center justify-center text-white font-semibold text-[13px] shrink-0"
                style={{
                  background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
                  boxShadow: "0 4px 14px -6px rgba(124,58,237,0.4)",
                }}
              >
                {(c.name || "—").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700 mb-0.5">
                  {c.role_label}
                </p>
                <p className="text-[13px] font-semibold text-slate-900 truncate">{c.name}</p>
                {c.organisation ? (
                  <p className="text-[11px] text-slate-500 truncate">{c.organisation}</p>
                ) : null}
                <div className="flex items-center gap-2 mt-2">
                  {c.email ? (
                    <a
                      href={`mailto:${c.email}`}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[10.5px] font-semibold transition-all"
                      style={{
                        background: "rgba(99,102,241,0.10)", color: "#4338CA",
                        border: "1px solid rgba(99,102,241,0.22)",
                      }}
                      data-testid={`client-contact-email-${c.role_key}`}
                    >
                      <Mail size={10} strokeWidth={2.4} />
                      Email
                    </a>
                  ) : null}
                  {c.phone ? (
                    <a
                      href={`tel:${c.phone}`}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[10.5px] font-semibold transition-all"
                      style={{
                        background: "rgba(148,163,184,0.10)", color: "#475569",
                      }}
                      data-testid={`client-contact-phone-${c.role_key}`}
                    >
                      <Phone size={10} strokeWidth={2.4} />
                      Call
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Exchange / Completion Prep ───────────────────────────────── */
const PrepBlock = ({ prep }) => {
  if (!prep) return null;
  return (
    <section className="mb-6" data-testid="client-prep">
      <SectionHeader title={prep.title} subtitle="Calm preparation steps." />
      <div className="gp-card p-5">
        <p className="text-[12.5px] text-slate-700 leading-relaxed mb-4">{prep.message}</p>
        {prep.exchange_date || prep.completion_date ? (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {prep.exchange_date ? (
              <DateBlock label="Target exchange" value={prep.exchange_date}
                days={prep.days_to_exchange} testid="client-prep-exchange" />
            ) : null}
            {prep.completion_date ? (
              <DateBlock label="Target completion" value={prep.completion_date}
                days={prep.days_to_completion} testid="client-prep-completion" />
            ) : null}
          </div>
        ) : null}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">Readiness</p>
            <p className="text-[12px] font-semibold tabular-nums text-violet-700">{prep.readiness_pct}%</p>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(148,163,184,0.12)" }}>
            <div className="h-full" style={{
              width: `${prep.readiness_pct}%`,
              background: "linear-gradient(90deg, #818CF8 0%, #10B981 100%)",
              transition: "width 320ms cubic-bezier(0.4,0,0.2,1)",
            }} />
          </div>
        </div>
        <div className="space-y-2">
          {(prep.checklist || []).map((it) => (
            <div key={it.key} className="flex items-start gap-2.5" data-testid={`client-prep-${it.key}`}>
              <div
                className="w-5 h-5 rounded-md inline-flex items-center justify-center shrink-0 mt-0.5"
                style={{
                  background: it.complete ? "rgba(16,185,129,0.14)" : "rgba(148,163,184,0.12)",
                  color: it.complete ? "#047857" : "#94A3B8",
                }}>
                {it.complete ? <CheckCircle2 size={11} strokeWidth={2.6} /> : <Circle size={9} strokeWidth={2.4} />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-slate-800">{it.label}</p>
                <p className="text-[10.5px] text-slate-500 leading-snug">{it.help}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DateBlock = ({ label, value, days, testid }) => (
  <div
    className="rounded-lg p-3"
    style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.16)" }}
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-violet-700">{label}</p>
    <p className="text-[14px] font-semibold tabular-nums text-slate-900">{value}</p>
    {days != null ? (
      <p className="text-[11px] text-slate-500">{days} day{days === 1 ? "" : "s"} away</p>
    ) : null}
  </div>
);

/* ─── Updates feed ─────────────────────────────────────────────── */
const UpdatesFeed = ({ items }) => {
  if (!items?.length) return null;
  return (
    <section className="mb-6" data-testid="client-updates">
      <SectionHeader title="Recent updates" subtitle="A simple history of what's been happening." />
      <div className="gp-card divide-y divide-slate-100">
        {items.map((u, i) => (
          <div key={`${u.type}-${i}`} className="px-4 py-3 flex items-center gap-3"
               data-testid={`client-update-${i}`}>
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <p className="text-[12.5px] font-medium text-slate-800 flex-1">{u.label}</p>
            <p className="text-[11px] text-slate-500 tabular-nums shrink-0">{u.happened}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Document Upload Dialog (drag-drop + mobile camera) ───────── */
const DocumentUploadDialog = ({ open, action, dealId, onClose, onUploaded, tokenScoped }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy]   = useState(false);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files || []);
    setFiles((prev) => [...prev, ...dropped]);
  }, []);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
  };

  const onUpload = async () => {
    if (!files.length || tokenScoped) return;
    setBusy(true);
    let succeeded = 0;
    try {
      for (const f of files) {
        const pre = clientPreflight(f);
        if (!pre.ok) {
          toast.error(`${pre.title} · ${f.name}`, { description: pre.reason });
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/api/deals/${dealId}/documents`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          succeeded += 1;
        } catch (err) {
          const { title, description } = uploadErrorPresentation(err, f.name);
          toast.error(title, { description });
        }
      }
      if (succeeded > 0) {
        toast.success(
          succeeded === files.length
            ? "Documents uploaded — thank you!"
            : `Uploaded ${succeeded} of ${files.length} — review the errors above for the rest.`,
        );
        setFiles([]);
        onUploaded?.();
        if (succeeded === files.length) onClose?.();
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(8,11,30,0.65)", backdropFilter: "blur(6px)" }}
      data-testid="client-upload-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md gp-card p-5"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#FFFFFF" }}
      >
        <h3 className="text-[16px] font-semibold text-slate-900 mb-1">
          {action?.label || "Upload document"}
        </h3>
        <p className="text-[12px] text-slate-600 mb-4">{action?.why}</p>

        {tokenScoped ? (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-3 mb-3">
            Uploads from a share link require signing in. Please ask your agent for access if needed.
          </p>
        ) : null}

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="rounded-xl p-6 text-center mb-3"
          style={{
            background: "rgba(99,102,241,0.05)",
            border: "2px dashed rgba(99,102,241,0.25)",
          }}
          data-testid="client-upload-dropzone"
        >
          <Upload size={20} className="text-violet-500 mx-auto mb-2" />
          <p className="text-[12.5px] font-semibold text-slate-800">Drop files here</p>
          <p className="text-[11px] text-slate-500 mb-3">or pick from your device</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <label
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] font-semibold cursor-pointer"
              style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA",
                       border: "1px solid rgba(99,102,241,0.22)" }}
            >
              <Upload size={11} strokeWidth={2.4} />
              Choose files
              <input
                type="file"
                multiple
                onChange={onPick}
                className="hidden"
                data-testid="client-upload-input"
              />
            </label>
            <label
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] font-semibold cursor-pointer sm:hidden"
              style={{ background: "rgba(16,185,129,0.10)", color: "#047857",
                       border: "1px solid rgba(16,185,129,0.22)" }}
            >
              <Camera size={11} strokeWidth={2.4} />
              Camera
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPick}
                className="hidden"
                data-testid="client-upload-camera"
              />
            </label>
          </div>
        </div>

        {files.length ? (
          <ul className="mb-4 space-y-1 max-h-32 overflow-auto">
            {files.map((f, i) => (
              <li key={i} className="text-[11.5px] text-slate-600 truncate" data-testid={`client-upload-file-${i}`}>
                · {f.name} ({Math.round(f.size / 1024)}KB)
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 rounded-md text-[12px] font-semibold text-slate-600"
            data-testid="client-upload-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onUpload}
            disabled={busy || !files.length || tokenScoped}
            className="h-9 px-3 rounded-md text-[12px] font-semibold text-white transition-all"
            style={{
              background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
              opacity: (busy || !files.length || tokenScoped) ? 0.55 : 1,
            }}
            data-testid="client-upload-submit"
          >
            {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Section Header ───────────────────────────────────────────── */
const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-3 px-1">
    <h2 className="text-[15px] sm:text-[16px] font-semibold text-slate-900">{title}</h2>
    {subtitle ? (
      <p className="text-[12px] text-slate-500 leading-snug">{subtitle}</p>
    ) : null}
  </div>
);

/* ─── Deal selector (for "/client-view" with no dealId) ─────────── */
const DealPicker = ({ deals }) => {
  const nav = useNavigate();
  return (
    <div className="min-h-screen px-5 sm:px-7 lg:px-9 pt-6 pb-12 max-w-2xl mx-auto"
         data-testid="client-deal-picker">
      <header className="mb-6">
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-violet-700 mb-2">
          Your Transactions
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
          Choose your transaction
        </h1>
        <p className="text-[13px] text-slate-500 mt-2">
          Tap a transaction to open its calm, simplified view.
        </p>
      </header>
      <div className="space-y-3">
        {(deals || []).map((d) => (
          <button
            type="button"
            key={d.deal_id}
            className="w-full gp-card p-4 text-left transition-all hover:shadow-md flex items-center gap-3"
            onClick={() => nav(`/client-view/${d.deal_id}`)}
            data-testid={`client-deal-${d.deal_id}`}
          >
            <div className="w-10 h-10 rounded-xl inline-flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #6366F1, #A855F7)", color: "#FFFFFF" }}>
              <Home size={14} strokeWidth={2.4} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13.5px] font-semibold text-slate-900 truncate">{d.name}</p>
              <p className="text-[11.5px] text-slate-500 capitalize">{(d.stage || "").replace(/_/g, " ")}</p>
            </div>
            <ChevronRight size={14} className="text-slate-300" />
          </button>
        ))}
        {(!deals || deals.length === 0) ? (
          <p className="text-[12px] text-slate-500 italic px-2">
            You don't have any active transactions visible right now.
          </p>
        ) : null}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Page
   ─────────────────────────────────────────────────────────────────── */
export default function ClientExperiencePage({ tokenScoped = false }) {
  const { dealId, token } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [deals, setDeals]       = useState(null);
  const [experience, setExp]    = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [refreshTick, setTick]  = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadAction, setUploadAction] = useState(null);

  const isList = !tokenScoped && !dealId;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        if (isList) {
          const { data } = await api.get("/api/client/experience/me");
          if (alive) setDeals(data?.deals || []);
        } else if (tokenScoped && token) {
          const { data } = await api.get(`/api/client/experience/by-token/${token}`);
          if (alive) setExp(data?.experience || null);
        } else if (dealId) {
          const { data } = await api.get(`/api/client/experience/deals/${dealId}`);
          if (alive) setExp(data?.experience || null);
        }
      } catch (err) {
        if (alive) {
          if (err?.response?.status === 403) setError("You don't have access to this transaction.");
          else if (err?.response?.status === 404) setError("This share link is no longer valid.");
          else setError("Could not load your transaction view. Please try again shortly.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isList, tokenScoped, token, dealId, refreshTick]);

  if (loading) {
    return (
      <div className="min-h-screen px-5 sm:px-7 lg:px-9 pt-6 pb-12 max-w-4xl mx-auto">
        <div className="rounded-3xl p-9  cf-pulse mb-6"
          style={{ background: "linear-gradient(135deg, #0F172A 0%, #2A2566 100%)" }}>
          <div className="h-3 w-32 bg-white/15 rounded mb-3" />
          <div className="h-7 w-56 bg-white/15 rounded mb-2" />
          <div className="h-4 w-72 bg-white/10 rounded" />
        </div>
        <div className="gp-card p-5 cf-pulse" data-testid="client-loading">
          <div className="h-3 w-40 bg-slate-100 rounded mb-3" />
          <div className="h-4 w-72 bg-slate-100 rounded mb-2" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen px-5 pt-12 pb-12 max-w-md mx-auto" data-testid="client-error">
        <div className="gp-card p-6 text-center">
          <ShieldCheck size={22} className="text-violet-500 mx-auto mb-3" />
          <p className="text-[13.5px] font-semibold text-slate-900 mb-1">{error}</p>
          <p className="text-[12px] text-slate-500">
            Please reach out to your agent if you need help.
          </p>
        </div>
      </div>
    );
  }

  if (isList) {
    return <DealPicker deals={deals} />;
  }

  if (!experience) return null;

  const onUploadClick = (action) => {
    setUploadAction(action);
    setUploadOpen(true);
  };

  const onBack = () => {
    if (tokenScoped) return;
    nav("/client-view");
  };

  return (
    <div
      className="min-h-screen pt-6 pb-12"
      style={{ background: "linear-gradient(180deg, #F8FAFF 0%, #FFFFFF 30%)" }}
      data-testid="client-experience-page"
    >
      <div className="px-5 sm:px-7 lg:px-9 max-w-4xl mx-auto">
        {!tokenScoped ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 mb-4 text-[12px] font-semibold text-violet-700"
            data-testid="client-back"
          >
            <ArrowLeft size={11} strokeWidth={2.4} />
            All transactions
          </button>
        ) : null}

        <ClientHero
          deal={experience.deal}
          stageView={experience.stage_view}
          reassurance={experience.reassurance}
        />
        <SimplifiedTimeline items={experience.timeline} />
        <ActionCentre actions={experience.actions} onUploadClick={onUploadClick} />
        <PrepBlock prep={experience.prep} />
        <KeyContacts contacts={experience.contacts} />
        <UpdatesFeed items={experience.updates} />

        <section
          className="mt-6 mb-2 p-5 rounded-2xl"
          style={{
            background: "linear-gradient(135deg, rgba(244,244,255,0.7), rgba(255,255,255,0.6))",
            border: "1px solid rgba(148,163,184,0.16)",
          }}
          data-testid="client-footer-note"
        >
          <div className="inline-flex items-center gap-2 mb-2">
            <MessageCircle size={11} className="text-violet-600" strokeWidth={2.3} />
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
              Need to talk?
            </p>
          </div>
          <p className="text-[12.5px] text-slate-700 leading-relaxed max-w-xl">
            Your team is here to support you. Use the contact cards above to reach the
            right person at any time during your transaction.
          </p>
        </section>
      </div>

      <DocumentUploadDialog
        open={uploadOpen}
        action={uploadAction}
        dealId={experience.deal?.id}
        tokenScoped={tokenScoped}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => setTick((t) => t + 1)}
      />
    </div>
  );
}
